import { z } from 'zod';

type T = (key: string) => string;

export const createBasicInfoSchema = (
  t: T,
  options?: { labMode?: 'single' | 'multi' }
) => {
  const labMode = options?.labMode ?? 'single';

  return z
    .object({
      name: z
        .string()
        .min(3, t('dynamicFormNameMin'))
        .max(100, t('dynamicFormNameMax'))
        .regex(/\p{L}/u, t('dynamicFormNameAlpha')),
      type: z.enum(['registration', 'filling'], {
        message: t('dynamicFormTypeRequired')
      }),
      is_default: z.boolean(),
      is_double_sided: z.boolean(),
      offices: z.array(z.string())
    })
    .refine(
      (data) =>
        data.type !== 'registration' ||
        data.offices.filter((o) => o && o.length > 0).length >= 1,
      {
        message: t('dynamicFormOfficesRequired'),
        path: ['offices']
      }
    )
    .refine(
      (data) =>
        labMode !== 'single' ||
        data.type !== 'registration' ||
        data.offices.length <= 1,
      {
        message: t('dynamicFormOfficesSingleOnly'),
        path: ['offices']
      }
    );
};

export const basicInfoSchema = createBasicInfoSchema((key) => {
  const defaults: Record<string, string> = {
    dynamicFormNameMin: 'Name must be at least 3 characters.',
    dynamicFormNameMax: 'Name must not exceed 100 characters.',
    dynamicFormNameAlpha: 'Name must contain at least one letter.',
    dynamicFormTypeRequired: 'Form type is required.',
    setAsDefault: 'Set as Default',
    dynamicFormOfficesSingleOnly:
      'Only one lab can be selected in single-lab mode.',
    dynamicFormOfficesRequired: 'Select at least one lab.'
  };
  return defaults[key] ?? key;
});

export type BasicInfoFormValues = z.infer<typeof basicInfoSchema>;

export const createFieldSchema = (t: T) =>
  z
    .object({
      label: z
        .string()
        .min(1, t('dynamicFieldLabelRequired'))
        .max(50, t('dynamicFieldLabelMax')),
      type: z.enum(
        [
          'short_text',
          'long_text',
          'number',
          'date',
          'selection_list',
          'yes_no'
        ],
        { message: t('dynamicFieldTypeRequired') }
      ),
      is_required: z.boolean(),
      options: z.array(z.string().max(100))
    })
    .refine(
      (data) =>
        data.type !== 'selection_list' ||
        data.options.filter((o) => o.trim()).length >= 2,
      {
        message: 'Selection list must have at least 2 options.',
        path: ['options']
      }
    );
